import { doc, getDoc, type DocumentData } from 'firebase/firestore'
import { firestore } from '../firebase'
import { FirebaseError } from 'firebase/app'

interface HasPath {
  path: string
}

export class CacheManager<T extends HasPath> {
  private cache: Map<string, { data: T; timestamp: number; unsubscribe?: () => void }>
  private ttl: number
  private maxSize: number
  private cleanupIntervalId?: number
  private isCleanupPaused: boolean
  private maxAge?: number

  constructor(ttl: number, maxSize: number, maxAge?: number) {
    if (ttl <= 0 || !Number.isFinite(ttl)) {
      throw new Error('TTL must be a positive finite number')
    }
    if (maxSize <= 0 || !Number.isFinite(maxSize)) {
      throw new Error('Max size must be a positive finite number')
    }
    this.cache = new Map()
    this.ttl = ttl
    this.maxSize = Math.max(10, maxSize) // Ensure minimum size
    this.maxAge = maxAge && maxAge > 0 ? maxAge : undefined
    this.isCleanupPaused = false
    this.startCleanupInterval()
  }

  // Expose the size of the cache
  get size(): number {
    return this.cache.size
  }

  /**
   * Starts periodic cleanup of expired entries.
   */
  private startCleanupInterval(): void {
    if (this.cleanupIntervalId !== undefined) return
    this.cleanupIntervalId = window.setInterval(() => {
      if (!this.isCleanupPaused) {
        this.cleanExpired()
      }
    }, 60 * 1000)
    console.log('Cache cleanup interval started')
  }

  /**
   * Pauses periodic cleanup (e.g., for offline mode).
   */
  pauseCleanup(): void {
    this.isCleanupPaused = true
    console.log('Cache cleanup paused')
  }

  /**
   * Resumes periodic cleanup.
   */
  resumeCleanup(): void {
    this.isCleanupPaused = false
    console.log('Cache cleanup resumed')
  }

  /**
   * Destroys the cache, clearing all entries and stopping cleanup.
   */
  destroy(): void {
    if (this.cleanupIntervalId !== undefined) {
      window.clearInterval(this.cleanupIntervalId)
      this.cleanupIntervalId = undefined
      console.log('Cache cleanup interval stopped')
    }
    this.clear()
  }

  /**
   * Retrieves a cache entry if it exists and is not expired.
   */
  get(key: string): T | null {
    if (!key) return null
    const entry = this.cache.get(key)
    if (!entry) return null
    const now = Date.now()
    if (
      now - entry.timestamp >= this.ttl ||
      (this.maxAge && now - entry.timestamp >= this.maxAge)
    ) {
      this.remove(key)
      return null
    }
    console.debug(`Cache hit for key: ${key}`)
    return entry.data
  }

  /**
   * Retrieves the full cache entry (including metadata) if it exists and is not expired.
   */
  getEntry(key: string): { data: T; timestamp: number; unsubscribe?: () => void } | null {
    if (!key) return null
    const entry = this.cache.get(key)
    if (!entry) return null
    const now = Date.now()
    if (
      now - entry.timestamp >= this.ttl ||
      (this.maxAge && now - entry.timestamp >= this.maxAge)
    ) {
      this.remove(key)
      return null
    }
    console.debug(`Cache entry retrieved for key: ${key}`)
    return entry
  }

  /**
   * Sets a cache entry, respecting maxSize and updating unsubscribe if provided.
   */
  set(key: string, data: T, unsubscribe?: () => void): void {
    if (!key || !data) {
      console.warn('Invalid cache set: key or data missing')
      return
    }
    if (!data.path) {
      console.warn(`Invalid cache data for key ${key}: missing path`)
      return
    }

    // Evict oldest entry if cache is full
    if (this.cache.size >= this.maxSize) {
      const oldestKey = Array.from(this.cache.keys())[0]
      this.remove(oldestKey)
    }

    const existingEntry = this.cache.get(key)
    const newEntry = {
      data,
      timestamp: Date.now(),
      unsubscribe: unsubscribe ?? existingEntry?.unsubscribe,
    }
    this.cache.set(key, newEntry)
    console.debug(`Cache set for key: ${key}`)
  }

  /**
   * Removes a cache entry and calls its unsubscribe function if present.
   */
  remove(key: string): void {
    if (!key) return
    const entry = this.cache.get(key)
    if (entry) {
      if (entry.unsubscribe) {
        try {
          entry.unsubscribe()
          console.debug(`Unsubscribed listener for key: ${key}`)
        } catch (error) {
          console.error(`Error unsubscribing cache entry ${key}:`, error)
        }
      }
      this.cache.delete(key)
      console.debug(`Cache removed for key: ${key}`)
    }
  }

  /**
   * Cleans expired entries based on TTL and maxAge.
   */
  cleanExpired(): void {
    const now = Date.now()
    let removedCount = 0
    for (const [key, entry] of this.cache.entries()) {
      if (
        now - entry.timestamp >= this.ttl ||
        (this.maxAge && now - entry.timestamp >= this.maxAge)
      ) {
        this.remove(key)
        removedCount++
      }
    }
    if (removedCount > 0) {
      console.log(`Cleaned ${removedCount} expired cache entries`)
    }
  }

  /**
   * Clears all cache entries and their listeners.
   */
  clear(): void {
    let clearedCount = 0
    for (const [key, entry] of this.cache.entries()) {
      if (entry.unsubscribe) {
        try {
          entry.unsubscribe()
          console.debug(`Unsubscribed listener for key: ${key}`)
        } catch (error) {
          console.error(`Error unsubscribing cache entry ${key}:`, error)
        }
      }
      this.cache.delete(key)
      clearedCount++
    }
    if (clearedCount > 0) {
      console.log(`Cleared ${clearedCount} cache entries`)
    }
  }

  /**
   * Resizes the cache to a new maxSize, evicting oldest entries if necessary.
   */
  resize(newSize: number): void {
    if (newSize <= 0 || !Number.isFinite(newSize)) {
      console.warn('Invalid resize: newSize must be a positive finite number')
      return
    }
    this.maxSize = Math.max(10, newSize)
    while (this.cache.size > this.maxSize) {
      const oldestKey = Array.from(this.cache.keys())[0]
      this.remove(oldestKey)
    }
    console.log(`Cache resized to maxSize: ${this.maxSize}`)
  }

  /**
   * Returns an array of all cache keys.
   */
  keys(): string[] {
    const keys = Array.from(this.cache.keys())
    console.debug(`Retrieved ${keys.length} cache keys`)
    return keys
  }

  /**
   * Returns an iterator over all cache entries, filtering out expired ones.
   */
  *entries(): IterableIterator<[string, { data: T; timestamp: number; unsubscribe?: () => void }]> {
    const now = Date.now()
    for (const [key, entry] of this.cache.entries()) {
      if (
        now - entry.timestamp >= this.ttl ||
        (this.maxAge && now - entry.timestamp >= this.maxAge)
      ) {
        this.remove(key)
        continue
      }
      yield [key, entry]
    }
  }

  /**
   * Refreshes a single cache entry from Firestore.
   */
  async refreshCacheEntry(key: string, transform?: (data: DocumentData) => T): Promise<void> {
    if (!key) return
    const entry = this.cache.get(key)
    if (!entry) return

    const path = entry.data.path
    try {
      const docRef = doc(firestore, path)
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        const rawData = docSnap.data()
        const updatedData = transform ? transform(rawData) : (rawData as T)
        if (!updatedData.path) {
          console.warn(`Refreshed data for ${key} missing path, removing entry`)
          this.remove(key)
          return
        }
        this.cache.set(key, { ...entry, data: updatedData, timestamp: Date.now() })
        console.debug(`Cache entry refreshed for key: ${key}`)
      } else {
        this.remove(key)
        console.debug(`Cache entry removed for key: ${key} (document not found)`)
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        if (error.code === 'resource-exhausted') {
          console.warn(`Firestore quota exceeded for key ${key}, skipping refresh`)
        } else {
          console.error(`Error refreshing cache entry ${key}:`, error)
        }
      } else {
        console.error(`Unexpected error refreshing cache entry ${key}:`, error)
      }
    }
  }

  /**
   * Refreshes all cache entries in batches to avoid Firestore rate limits.
   */
  async refreshAllEntries(): Promise<void> {
    const keys = Array.from(this.cache.keys())
    const batchSize = 10
    console.log(`Refreshing ${keys.length} cache entries`)

    for (let i = 0; i < keys.length; i += batchSize) {
      const batchKeys = keys.slice(i, i + batchSize)
      const refreshPromises = batchKeys.map((key) => this.refreshCacheEntry(key))
      try {
        await Promise.all(refreshPromises)
      } catch (error: unknown) {
        if (error instanceof FirebaseError) {
          console.error('Firebase error during batch refresh:', error)
        } else {
          console.error('Unexpected error during batch refresh:', error)
        }
      }
      // Add a delay to avoid hitting Firestore rate limits
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
    console.log(`Completed refreshing ${keys.length} cache entries`)
  }
}
